/**
 * The single source of truth for app identity (ADR-072).
 *
 * A developer deriving a new app from this template edits this one module and
 * the whole app renames itself: page titles, email subjects and bodies, and
 * meta tags all read from here. The JSON carriers that can't import TS —
 * `CloudronManifest.json`, `public/site.webmanifest`, `package.json` — are
 * hand-edited to agree with these values, and `brand.test.ts` fails CI if they
 * drift or if a stray hardcoded identity string survives under `app/`.
 *
 * This is static identity only. Operational values (URLs the platform injects,
 * secrets, SMTP endpoints) stay in the environment — see `env.server.ts`.
 *
 * Identity splits along a linguistic line (ADR-074, amending ADR-072's shape):
 * `appName` and the non-linguistic fields are single strings — a product is
 * named once — while the marketing copy (`tagline`, `description`) is a
 * per-locale record keyed off the supported-locale registry. Consumers select
 * the entry for the resolved locale; the single-language JSON carriers pin the
 * Default Language entry. Typing the records off `Locale` means adding a locale
 * to the registry is a compile error here until its entries exist.
 */
import { type Locale } from './utils/i18n.ts'

export const brand = {
	/**
	 * The product name. Read at runtime by page titles and email subjects/bodies,
	 * and mirrored into the JSON carriers (CloudronManifest `title`, webmanifest
	 * `name`/`short_name`). Not linguistic — one string in every locale.
	 */
	appName: 'Epic App',
	/**
	 * The product slogan, per locale. Rendered locale-aware in root meta;
	 * the Default Language entry is mirrored into CloudronManifest `tagline`.
	 */
	tagline: {
		de: 'Basiert auf dem Epic Stack, ausgeliefert auf Cloudron',
		en: 'Built on the Epic Stack, deployed to Cloudron',
	},
	/**
	 * The product description, per locale. Rendered locale-aware in root meta;
	 * the Default Language entry is mirrored into CloudronManifest `description`.
	 */
	description: {
		de: 'Eine Epic-Stack-Anwendung',
		en: 'An Epic Stack application',
	},
	/** Mirrored into CloudronManifest `website`; no runtime consumer yet. */
	canonicalUrl: 'https://demo.opensourced.ch',
	/**
	 * Mirrored into CloudronManifest `contactEmail`; becomes the outbound-email
	 * reply-to once the Cloudron SMTP slice (EPT-122) wires the mail transport.
	 */
	contactEmail: 'you@example.com',
	/** Display name outbound email is sent as — consumed by EPT-122's transport. */
	emailSenderName: 'Epic App',
} as const satisfies {
	appName: string
	tagline: Record<Locale, string>
	description: Record<Locale, string>
	canonicalUrl: string
	contactEmail: string
	emailSenderName: string
}

export type Brand = typeof brand
